import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { STELE_SPACE_KIND, STELE_CLIENT, isTombstone, isSteleSpace } from '@/lib/stele';

/** Fetch all spaces owned by a given pubkey */
export function useSpaces(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent[]>({
    queryKey: ['nostr', 'stele', 'spaces', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return [];
      const events = await nostr.query(
        [{ kinds: [STELE_SPACE_KIND], authors: [pubkey], '#client': [STELE_CLIENT] }],
        { signal: c.signal },
      );
      // Client-side validation: relays may not perfectly filter multi-tag queries
      return events.filter(e => isSteleSpace(e) && !isTombstone(e.tags));
    },
    enabled: !!pubkey,
  });
}

/** Fetch a single space by pubkey + slug */
export function useSpace(pubkey: string | undefined, spaceSlug: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<NostrEvent | null>({
    queryKey: ['nostr', 'stele', 'space', pubkey ?? '', spaceSlug ?? ''],
    queryFn: async (c) => {
      if (!pubkey || !spaceSlug) return null;
      const dTag = `stele:space:${spaceSlug}`;
      const events = await nostr.query(
        [{ kinds: [STELE_SPACE_KIND], authors: [pubkey], '#d': [dTag], '#client': [STELE_CLIENT], limit: 1 }],
        { signal: c.signal },
      );
      const event = events[0];
      if (!event || !isSteleSpace(event) || isTombstone(event.tags)) return null;
      return event;
    },
    enabled: !!pubkey && !!spaceSlug,
  });
}
